import { useEffect } from 'react';
import { Box, VStack, Spinner, HStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { BaseWidgetProps } from '../widgets';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { ProductApprovalRatioOptions, WidgetType } from '../shared/types';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { statisticsApi } from '@texas/api/endpoints/statisticsApi';
import { RatioChart } from './RatioChart';
import { StatisticsHeaderFilters } from '../shared/StatisticsHeaderFilters';
import { useWidgetStorage } from '../shared/useWidgetStorage';

interface Props extends BaseWidgetProps {
  options?: ProductApprovalRatioOptions;
}

export function ProductApprovalRatioWidget({
  color,
  customTitle,
  widgetId,
  options,
}: Props) {
  const { t } = useTranslation();
  const { filters, updateSampleSize, updateCustomerId, updateBranchIds } =
    useWidgetStorage(widgetId);

  const { data, loading, refetch } = useApiResource(
    statisticsApi.getProductApprovalRatio,
  );

  const selectedCustomerId = options?.customerId ?? filters.customerId;
  const selectedBranchIds =
    options?.branchIds && options.branchIds.length > 0
      ? options.branchIds
      : filters.branchIds;

  useEffect(() => {
    refetch({
      sampleSize: filters.sampleSize,
      customerId: selectedCustomerId ?? undefined,
      branchIds: selectedBranchIds.length > 0 ? selectedBranchIds : undefined,
    });
  }, [filters.sampleSize, selectedCustomerId, selectedBranchIds, refetch]);

  return (
    <WidgetWrapper
      widgetId={widgetId}
      loading={loading}
      customColor={color}
      type={WidgetType.ProductApprovalRatio}
      style="default"
      header={customTitle ?? t('widgets.paRatio.title')}
      toolbox={
        <HStack spacing={2} overflowX="auto">
          <StatisticsHeaderFilters
            sampleSize={filters.sampleSize}
            onSampleSizeChange={updateSampleSize}
            selectedCustomerId={selectedCustomerId}
            onCustomerChange={updateCustomerId}
            selectedBranchIds={selectedBranchIds}
            onBranchChange={updateBranchIds}
            isCustomerLocked={!!options?.customerId}
            isBranchLocked={
              !!options?.branchIds && options.branchIds.length > 0
            }
          />
        </HStack>
      }
    >
      <VStack align="stretch" spacing={4} height="100%">
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <Spinner />
          </Box>
        ) : (
          <RatioChart data={data?.productGroups ?? []} />
        )}
      </VStack>
    </WidgetWrapper>
  );
}
